/**
 * The **value editor** of one condition row (`specs/M1-B6-filters-sort.md` §4.3).
 *
 * monday renders a different third control per condition, not per column type: the same
 * `date` column shows a date picker for `is after`, two pickers for `is between` and a plain
 * number of days for `in the last`. The column type therefore declares *what the editor must
 * collect* (`FilterCondition.value`) and this component renders it — one place, eight kinds,
 * no `switch (columnType)` anywhere (CLAUDE.md rule 6).
 *
 * A type that wants its own editor still wins: `ColumnTypeUi.FilterEditor` is used when the
 * type provides one (`[B5§6]`), and this is the fall-back for the 30-odd that do not.
 */
import type { ComponentType } from 'react';
import type { FilterCondition } from '@elizmonday/shared';
import type { BoardColumn, Person } from '../../data/types';
import type { CellBucket, FilterEditorProps } from '../../columns/types';

export interface FilterValueEditorProps {
  readonly condition: FilterCondition | undefined;
  readonly column: BoardColumn | undefined;
  readonly buckets: readonly CellBucket[];
  readonly people: readonly Person[];
  readonly value: unknown;
  onChange(next: unknown): void;
  readonly FilterEditor?: ComponentType<FilterEditorProps>;
}

/** The list form every multi-value operator stores, whatever the editor collected. */
function asList(value: unknown): unknown[] {
  if (value === null || value === undefined) return [];
  return Array.isArray(value) ? [...(value as unknown[])] : [value];
}

function first(value: unknown): string {
  const list = asList(value);
  return list.length === 0 ? '' : String(list[0]);
}

function second(value: unknown): string {
  const list = asList(value);
  return list.length < 2 ? '' : String(list[1]);
}

export function FilterValueEditor({
  condition,
  column,
  buckets,
  people,
  value,
  onChange,
  FilterEditor,
}: FilterValueEditorProps) {
  // `is empty` / `is checked` / `is assigned` take no value at all — measured.
  if (!condition || condition.value === 'none') {
    return <span className="ez-filter-rule__value" data-testid="filter-value-none" />;
  }

  if (FilterEditor && column) {
    return (
      <span className="ez-filter-rule__value">
        <FilterEditor
          settings={column.settings}
          column={column}
          value={value}
          buckets={buckets}
          onChange={onChange}
        />
      </span>
    );
  }

  const kind = condition.value;

  if (kind === 'options') {
    // Multi-select: monday's `is` on a status accepts several labels, OR-ed inside the rule.
    const selected = asList(value).map(String);
    return (
      <span className="ez-filter-rule__value">
        <select
          aria-label="Value"
          multiple
          size={Math.min(Math.max(buckets.length, 2), 5)}
          data-testid="filter-value-options"
          value={selected}
          onChange={(event) =>
            onChange([...event.target.selectedOptions].map((option) => option.value))
          }
        >
          {buckets.map((bucket) => (
            <option key={String(bucket.key)} value={String(bucket.key)}>
              {bucket.label}
            </option>
          ))}
        </select>
      </span>
    );
  }

  if (kind === 'people') {
    const selected = asList(value).map(String);
    return (
      <span className="ez-filter-rule__value">
        <select
          aria-label="Value"
          multiple
          size={Math.min(Math.max(people.length, 2), 5)}
          data-testid="filter-value-people"
          value={selected}
          onChange={(event) =>
            onChange([...event.target.selectedOptions].map((option) => option.value))
          }
        >
          {people.map((person) => (
            <option key={String(person.id)} value={String(person.id)}>
              {person.name}
            </option>
          ))}
        </select>
      </span>
    );
  }

  if (kind === 'dateRange') {
    return (
      <span className="ez-filter-rule__value ez-filter-rule__value--range">
        <input
          aria-label="Value from"
          type="date"
          data-testid="filter-value-from"
          value={first(value)}
          onChange={(event) => onChange([event.target.value, second(value)])}
        />
        <input
          aria-label="Value to"
          type="date"
          data-testid="filter-value-to"
          value={second(value)}
          onChange={(event) => onChange([first(value), event.target.value])}
        />
      </span>
    );
  }

  const inputType = kind === 'date' ? 'date' : kind === 'number' || kind === 'days' ? 'number' : 'text';
  return (
    <span className="ez-filter-rule__value">
      <input
        aria-label="Value"
        type={inputType}
        data-testid={`filter-value-${kind}`}
        // `in the last` / `in the next` collect a count of days, so the unit is spelled out.
        placeholder={kind === 'days' ? 'days' : undefined}
        value={first(value)}
        onChange={(event) => onChange(event.target.value === '' ? null : [event.target.value])}
      />
    </span>
  );
}
